/**
 * Header 會員區塊島嶼（計劃 14 T-14-6）。
 *
 * ════════════════════════════════════════════════════════════════════════════════
 * 🔴 這個島嶼存在的理由是**快取安全**，不是互動需求
 * ════════════════════════════════════════════════════════════════════════════════
 *
 * 計劃 08 T-08-2：只要 header 在 SSR 依登入狀態渲染不同 HTML，
 * 那一頁的 L1 快取版本就會把某個人的畫面發給所有人
 * （規格 2.5.1 稱之為 output caching 最常見的資安事故）。
 *
 * 因此 SSR 一律吐「未登入」的樣子 —— **對所有人完全相同** ——
 * 由這支在前端問 `GET /api/v1/me`（`no-store`）之後才換掉。
 * 這樣快取安全從「靠設定正確」變成「結構上不可能出錯」。
 *
 * ⚠️ **這是全站唯一掛在 Layout 上的島嶼**，也就是它讓每一頁都不再是「零 JS」
 * （規格 10.2）。那是 T-14-6 明確接受的代價 —— 沒有它，
 * 全站就不能安全地進 L1 快取。**所以請保持它極小。**
 *
 * ────────────────────────────────────────────────────────────────
 * 失敗時的行為
 * ────────────────────────────────────────────────────────────────
 *
 * 任何一步失敗（JS 關閉、`/me` 掛掉、網路斷、爬蟲不執行 JS）都**停在 SSR 的樣子**，
 * 也就是「未登入」。看起來像沒登入，而不是壞掉的畫面。
 * ⚠️ 刻意不顯示錯誤訊息 —— header 上跳一個紅字對使用者沒有任何幫助。
 */

interface MeResponse {
  isLoggedIn: boolean;
  memberId?: number;
  memberType?: string;
  displayName?: string;

  /**
   * 收件匣未讀數（稿面的「(300)訊息」，2026-09-21 補）。
   *
   * ⚠️ **印完整數字、不封頂**（業主 2026-09-21 指示）。
   * 後端有 20 秒快取（同舊站），所以讀完一封信之後這個數字最多晚 20 秒才變。
   */
  unreadMessages?: number;
}

/** SSR 端的容器。`data-member-bar` 在 `_Header.cshtml`。 */
const CONTAINER = '[data-member-bar]';

/**
 * 建一個連結。
 *
 * ⚠️ 用 `textContent` 而不是 `innerHTML` —— `displayName` 來自資料庫，
 * 是**使用者自己填的暱稱**。用 innerHTML 就是把 74,774 個人的自訂字串
 * 當成 HTML 執行（儲存型 XSS）。
 */
function link(href: string, text: string, className: string): HTMLAnchorElement {
  const a = document.createElement('a');
  a.href = href;
  a.textContent = text;
  a.className = className;
  return a;
}

function separator(): HTMLSpanElement {
  const span = document.createElement('span');
  span.className = 'text-muted';
  span.setAttribute('aria-hidden', 'true');
  span.textContent = '．';
  return span;
}

/**
 * 換成登入後的樣子，**兩行**：
 *
 * ```
 * 👤 {稱呼}
 * 會員專區．登出
 * ```
 *
 * ⚠️ 版面照稿（`主頁-Desktop`：`👤 P-Family編輯 (300)訊息` ／ `會員專區 | 登出`，稿面本來就是兩行）。
 * ✅ 稿面的「(300)訊息」已於 2026-09-21 補上。當初不做的兩個理由都解除了：
 *    訊息頁做好了、後端加了 20 秒快取（秒數照舊站，最重的帳號 45,152 封未讀要 73–82ms）。
 *
 * ⚠️ **未讀數不參與截斷** —— 名字放不下時截名字，數字一定要看得見。
 *    它是這一行裡唯一會變的資訊，截掉就失去意義了。
 *
 * ════════════════════════════════════════════════════════════════════════════════
 * 🔴 **導覽列不能因為登入而換行**（業主 2026-09-10：「以選單不要掉下來為優先」）
 * ════════════════════════════════════════════════════════════════════════════════
 *
 * 原本登入後擠成一行 `👤 稱呼．會員專區．登出`，實測（1440 寬）：
 *
 * | | 會員區寬 | 導覽寬 | 第一列高 | 搜尋列 |
 * |---|---|---|---|---|
 * | 未登入 | 113px | 696px | 31px | y=43 |
 * | 登入後（舊） | **164px** | 645px → **換行** | **68px** | **y=80** |
 *
 * ⇒ 不只「更多」掉下去，**整條搜尋列往下推了 37px**（版面位移）。
 *
 * 因此兩個硬限制：
 * <ol>
 *   <li>**寬度不得超過未登入時那一塊** —— 換掉之前先量 SSR 的寬度，
 *     登入後的區塊以它為上限。導覽可用的寬度因此前後完全相同。
 *     ⚠️ 刻意用**執行時量到的值**而不是寫死 113px：字型或文案一改，寫死的數字就錯了，
 *     而錯的樣子就是這次這個 bug。</li>
 *   <li>**高度不得超過原本的列高** —— 兩行各 15px 行高，擠進原本 31px 的列裡。</li>
 * </ol>
 *
 * ⚠️ 暱稱放不下就截斷加「…」，滑鼠停上去看全名（`title`）。
 * 舊帳號的暱稱欄是 nvarchar(50)，一定有放不下的人 —— 優先序是導覽不換行，不是暱稱完整。
 *
 * ════════════════════════════════════════════════════════════════════════════════
 * 🔴 **量測要跟著視窗寬度重做**（0910-1 問題 #6）
 * ════════════════════════════════════════════════════════════════════════════════
 *
 * 上面那個上限原本**只在載入當下量一次**。SSR 那一塊是 `lg:inline`，
 * 也就是**窄螢幕時它是 display:none、寬度 0** —— 量到 0 就不設上限。
 * 於是在 < 1024 載入、再把視窗拉寬（iPad 直拿轉橫拿、或桌機拉寬視窗）的人，
 * 會員區沒有上限 ⇒ 實測主選單掉成 **3 行**、搜尋列從 y=49 被推到 y=123，
 * 正是這個機制要防的那件事。
 *
 * 作法：留一份 SSR 那一塊的**複本當量尺**，寬度變了就重量一次。
 * ⚠️ 量尺是離畫面的複本而不是原件 —— 原件已經被 `replaceChildren()` 換掉了，
 *    而「登入後的區塊不得比未登入時寬」這個上限必須拿未登入的樣子去量。
 */
function renderSignedIn(container: Element, me: MeResponse): void {
  const name = me.displayName?.trim();

  // ① 換掉之前先留一份 SSR「加入會員．會員登入」那一塊當量尺。
  //    ⚠️ 不是只量一次就丟 —— 視窗拉寬之後要能再量（見上方說明）。
  const ssrDesktop = container.querySelector<HTMLElement>('.lg\\:inline');
  const ruler = ssrDesktop?.cloneNode(true) as HTMLElement | undefined;

  container.replaceChildren();

  // 行動版：圖示直接連會員專區（未登入時它連登入頁）。
  const icon = link('/member', '👤', 'lg:hidden');
  icon.setAttribute('aria-label', name ? `會員專區（${name}）` : '會員專區');
  icon.textContent = '👤';
  container.append(icon);

  // ⚠️ `lg:flex`（不是 lg:inline）：兩行要直向堆疊。
  const desktop = document.createElement('span');
  desktop.className = 'p9-member-signed-in hidden flex-col lg:flex';

  applyMaxWidth(container, desktop, ruler);
  watchWidth(() => applyMaxWidth(container, desktop, ruler));

  // 第一行：`👤 {稱呼} ({N})訊息`
  //
  // ⚠️ 用 flex 讓名字可以截斷而數字不會（CSS `.p9-member-line`）——
  //    `.p9-member-signed-in > *` 設的是 display:block，特異性要壓過它。
  const line = document.createElement('span');
  line.className = 'p9-member-line';

  if (name) {
    // ⚠️ 稱呼本身不是連結 —— 稿面它也不是。它只是「你是誰」。
    const who = document.createElement('span');
    who.className = 'p9-member-name text-heading';
    who.textContent = `👤 ${name}`;
    who.title = name;
    line.append(who);
  }

  line.append(messagesLink(me.unreadMessages ?? 0));
  desktop.append(line);

  const actions = document.createElement('span');
  actions.className = 'whitespace-nowrap';
  actions.append(
    link('/member', '會員專區', 'text-link hover:underline'),
    separator(),
    logoutButton(),
  );

  desktop.append(actions);
  container.append(desktop);
}

/**
 * 量一次「未登入時那一塊」的寬度，套成登入區塊的上限（0910-1 問題 #6）。
 *
 * ⚠️ 量尺要**掛回同一個容器**才量得準 —— 字型、字距都是繼承來的。
 * 掛上去的這一瞬間它是離畫面且 `aria-hidden`，而且在同一個同步區段裡就移除，
 * 不會被看到、不會進無障礙樹、也不會多出可點的連結。
 *
 * ⚠️ 量到 0 ＝ 現在是窄螢幕（SSR 那塊 `lg:inline` 被隱藏）。此時**清掉上限**
 * 而不是留著上一次的值：登入區塊在窄螢幕本來就不顯示，留著反而會在下一次
 * 拉寬時先套到一個過期的數字。
 */
function applyMaxWidth(
  container: Element,
  desktop: HTMLElement,
  ruler: HTMLElement | undefined,
): void {
  const width = measure(container, ruler);

  if (width > 0) {
    // ⚠️ 捨去不是進位 —— 進位會比未登入時多出不到 1px，「不得比未登入寬」要是硬保證。
    desktop.style.maxWidth = `${Math.floor(width)}px`;
  } else {
    desktop.style.removeProperty('max-width');
  }
}

function measure(container: Element, ruler: HTMLElement | undefined): number {
  if (!ruler) {
    return 0;
  }

  ruler.setAttribute('aria-hidden', 'true');
  ruler.style.position = 'absolute';
  ruler.style.left = '-9999px';
  ruler.style.top = '0';
  ruler.style.visibility = 'hidden';
  ruler.style.pointerEvents = 'none';

  container.append(ruler);
  const width = ruler.getBoundingClientRect().width;
  ruler.remove();

  return width;
}

/**
 * 視窗寬度變了就回呼一次（每一幀最多一次）。
 *
 * ⚠️ 只在**寬度**真的變了才做 —— 行動版瀏覽器捲動時工具列收合會觸發 resize，
 * 那只有高度在變，重量一次毫無意義。
 * ⚠️ `ResizeObserver` 而非 `window.onresize`：縮放、分割畫面、鍵盤彈出
 * 都會改變版面寬度，而那些不一定發 resize 事件。
 */
function watchWidth(onChange: () => void): void {
  if (typeof ResizeObserver !== 'function') {
    return;
  }

  let last = document.documentElement.clientWidth;
  let queued = false;

  new ResizeObserver(() => {
    const width = document.documentElement.clientWidth;

    if (width === last || queued) {
      return;
    }

    last = width;
    queued = true;

    requestAnimationFrame(() => {
      queued = false;
      onChange();
    });
  }).observe(document.documentElement);
}

/**
 * 「({N})訊息」—— 稿面第一行的右半（`主頁-Desktop`）。
 *
 * ⚠️ **未讀 0 時只印「訊息」，不印 `(0)`** —— 一直看到括號裡的 0 沒有意義，
 * 但入口本身要留著（不然登入後反而找不到訊息在哪）。
 *
 * ⚠️ **不封頂**（業主 2026-09-21 指示「印完整數字」）。
 * 舊站有一段「超過 9,999 就顯示 0」的防呆寫壞了，新站沒有搬。
 *
 * ⚠️ `shrink-0`：名字放不下時截名字，這個數字不能被擠掉（見 renderSignedIn 的說明）。
 */
function messagesLink(unread: number): HTMLAnchorElement {
  const text = unread > 0 ? `(${unread})訊息` : '訊息';

  const a = link('/member/messages', text, 'shrink-0 text-link hover:underline');

  if (unread > 0) {
    a.setAttribute('aria-label', `訊息，${unread} 則未讀`);
  }

  return a;
}

/**
 * 登出。
 *
 * ⚠️ 是 `<button>` 送 **POST**，不是連結。舊站用的是
 * `/index.aspx?action=logout`（GET）—— 那代表任何人只要讓你載入一張
 * `<img src="…action=logout">` 就能把你登出。
 * 雖然危害不高，但用 POST 是零成本的，沒有理由照抄。
 *
 * ⚠️ 成功後 `location.reload()`：頁面上可能有依登入狀態產生的內容
 * （目前沒有，但日後會有），整頁重載是唯一保證一致的作法。
 */
function logoutButton(): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'text-link hover:underline';
  button.textContent = '登出';

  button.addEventListener('click', () => {
    button.disabled = true;

    void fetch('/api/v1/auth/logout', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'fetch' },
    })
      .catch(() => undefined)
      .finally(() => window.location.reload());
  });

  return button;
}

async function run(): Promise<void> {
  const container = document.querySelector(CONTAINER);

  if (!container) {
    return;
  }

  try {
    const response = await fetch('/api/v1/me', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      return;
    }

    const me = (await response.json()) as MeResponse;

    // ⚠️ 未登入就**什麼都不做** —— SSR 已經是正確的樣子了。
    //    重畫一次相同的東西只會多一次版面計算。
    if (me.isLoggedIn) {
      renderSignedIn(container, me);
    }
  } catch {
    // 見檔頭：任何失敗都停在 SSR 的「未登入」樣子。
  }
}

void run();
